import * as React from "react";
import { trpc } from "src/trpc";
import styled from "styled-components";
import { compareAsc, formatDate } from "date-fns";
import { Archive, Calendar, FileUp, Lock, Scissors, ShoppingCart, Workflow } from "lucide-react";
import { UploadTransactions } from "./UploadTransactions";
import { RuleForm, Props as RuleFormProps } from "../RuleForm";
import { v4 as uuid } from "uuid";
import { Header } from "src/components/Header";
import { TransactionForm } from "../TransactionForm";
import { formatCurrency } from "src/utils/currency";
import { useIsoContext } from "src/isoContext";
import { SplitTransactionForm } from "../SplitTransactionForm";
import classNames from "classnames";

interface Props {
  month: string;
  categoryId: string | null;
}

export function TransactionsTab({ month, categoryId }: Props) {
  const [isUploading, setUploading] = React.useState(false);
  const [group, setGroup] = React.useState(true);
  const { isLarge } = useIsoContext();
  const [editRule, setEditRule] = React.useState<RuleFormProps["rule"] | null>(null);
  const [editTransaction, setEditTransaction] = React.useState<
    NonNullable<typeof results>[number] | null
  >(null);
  const [splitTransaction, setSplitTransaction] = React.useState<
    NonNullable<typeof results>[number] | null
  >(null);
  const { data } = trpc.getTransactions.useQuery({
    month,
    categoryId,
  });

  const { results = [] } = data ?? {};

  const createRule = (tx: NonNullable<typeof results>[number]) => {
    setEditRule(
      tx.rule ?? {
        id: uuid(),
        match: tx.description,
        label: tx.description,
        categoryId: null,
      },
    );
  };

  const sections: Array<{
    title: string;
    total: number;
    transactions: typeof results;
  }> = [];
  if (group) {
    for (const result of results) {
      const title = result.rule?.label ?? result.description;
      const section = sections.find((s) => s.title === title);
      if (section) {
        section.transactions.push(result);
        section.total += result.amount;
      } else {
        sections.push({ title, total: result.amount, transactions: [result] });
      }
    }
    sections.sort((a, b) => a.total - b.total);
    for (const section of sections) {
      section.transactions.sort((a, b) => compareAsc(a.date, b.date));
    }
  } else {
    for (const result of results) {
      const title = formatDate(result.date, "E do");
      if (sections.length === 0 || sections[sections.length - 1].title !== title) {
        sections.push({ title, total: result.amount, transactions: [result] });
      } else {
        sections[sections.length - 1].transactions.push(result);
        sections[sections.length - 1].total += result.amount;
      }
    }
  }

  return (
    <Container>
      {isLarge && (
        <Header slim title="Transactions">
          <Header.Button slim icon={FileUp} onClick={() => setUploading(true)}>
            Upload
          </Header.Button>
          <Header.Button
            slim
            icon={group ? Calendar : ShoppingCart}
            onClick={() => setGroup(!group)}
          >
            {group ? "Calendar View" : "Vendor View"}
          </Header.Button>
        </Header>
      )}
      <TableWrapper>
        <table style={{ alignSelf: "flex-start", width: "100%" }}>
          <tbody>
            {sections.map(({ title, total, transactions }, i) => {
              return (
                <React.Fragment key={title}>
                  {i > 0 && (
                    <Gap>
                      <td colSpan={isLarge ? 6 : 5}></td>
                    </Gap>
                  )}
                  <DateHeader>
                    <DateRow>{title}</DateRow>
                    {isLarge && <td></td>}
                    <DateTotal style={{ color: total < 0 ? "red" : "green" }}>
                      {formatCurrency(Math.abs(total))}
                    </DateTotal>
                    <td colSpan={3}></td>
                  </DateHeader>
                  {transactions.map((tx, i) => (
                    <TransactionRow className={classNames({ odd: i % 2 === 1 })} key={tx.id}>
                      <td style={{ whiteSpace: "pre" }}>
                        {group ? formatDate(tx.date, "E do") : (tx.rule?.label ?? tx.description)}
                      </td>
                      {isLarge && (
                        <td style={{ color: "#999" }}>
                          <div
                            style={{
                              display: "flex",
                              gap: "5px",
                              alignItems: "center",
                            }}
                          >
                            <div>{tx.category?.name}</div>
                            {tx.override && <Lock size={14} color="#999" />}
                          </div>
                        </td>
                      )}
                      <td
                        style={{
                          color: tx.amount >= 0 ? "green" : "red",
                          textAlign: "right",
                          fontFamily: "monospace",
                          fontSize: "12px",
                          fontWeight: "normal",
                        }}
                      >
                        {formatCurrency(Math.abs(tx.amount))}
                      </td>
                      <td className="action">
                        <Button onClick={() => createRule(tx)}>
                          <Workflow
                            size={14}
                            strokeWidth={tx.rule ? 2.5 : 2}
                            color={tx.rule ? "green" : "#999"}
                          />
                        </Button>
                      </td>
                      <td className="action">
                        <Button onClick={() => setEditTransaction(tx)}>
                          <Archive
                            size={14}
                            strokeWidth={tx.categoryId ? 2.5 : 2}
                            color={tx.categoryId ? "green" : "#999"}
                          />
                        </Button>
                      </td>
                      <td className="action">
                        <Button onClick={() => setSplitTransaction(tx)}>
                          <Scissors
                            size={14}
                            strokeWidth={tx.categoryId ? 2.5 : 2}
                            color={tx.categoryId ? "green" : "#999"}
                          />
                        </Button>
                      </td>
                    </TransactionRow>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </TableWrapper>

      {isUploading && <UploadTransactions onClose={() => setUploading(false)} />}
      {editRule && <RuleForm rule={editRule} onClose={() => setEditRule(null)} />}
      {editTransaction && (
        <TransactionForm transaction={editTransaction} onClose={() => setEditTransaction(null)} />
      )}
      {splitTransaction && (
        <SplitTransactionForm
          transaction={splitTransaction}
          onClose={() => setSplitTransaction(null)}
        />
      )}
    </Container>
  );
}

const Container = styled.div``;

const Gap = styled.tr`
  td {
    height: 15px;
  }
`;

const TableWrapper = styled.div`
  margin-bottom: 20px;
`;

const DateRow = styled.td`
  font-weight: 400;
  flex-grow: 1;
  padding: 5px 10px;
  font-size: 14px;
`;

const DateTotal = styled.td`
  font-family: monospace;
  font-size: 12px;
  text-align: right;
  padding: 5px 10px;
  background: var(--box-color);
`;

const TransactionRow = styled.tr`
  > td {
    padding: 5px 10px;
    font-size: 14px;
    font-weight: 300;
  }
  &.odd > td {
    background: rgba(0, 0, 0, 0.2);
  }
  > td.action {
    padding: 0;
    width: 0px;
  }
`;

const DateHeader = styled.tr`
  background: var(--box-color);
`;

const Button = styled.button`
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  font-weight: 500;
  padding: 3px 6px;
  white-space: nowrap;
`;
